import React, { useEffect, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import { getAuthHeaders } from "../../utils/auth";
import "../../style/finishorder.css";
import { apiAxios } from "../../utils/apiAxios";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Order {
  patientID: number;
  patientName: string;
  age: number;
  gender: string;
  tooth: string;
  materialName: string;
  shadeName: string;
  createdDate: string;
  status: string;
  clinicName?: string;
  mobileNumber?: string;
  emailID?: string;
  deliveryPartnerID?: number;
  deliveryPartnerName?: string;
}

interface ApiResponse {
  statusCode: number;
  isSuccess: boolean;
  data: Order[];
  singleData: any;
  status: string;
}

interface DeliveryPartner {
  deliveryPartnerID: number;
  name: string;
  mobileNumber: string;
  email: string;
}

interface OTPStatus {
  patientID: number;
  orderID: number;
  deliveryPartnerAssigned: boolean;
  deliveryPartnerName: string;
  deliveryPartnerMobile: number;
  pickupOTPVerified: boolean;
  deliveryOTPVerified: boolean;
  currentStatus: string;
  orderCompleted: boolean;
  dentistEmail: string;
  dentistMobile: string;
}

const FinishedOrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [deliveryPartners, setDeliveryPartners] = useState<DeliveryPartner[]>([]);
  const [showAssignModal, setShowAssignModal] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedPartner, setSelectedPartner] = useState<number>(0);
  const [assignLoading, setAssignLoading] = useState<boolean>(false);


  const [showOTPModal, setShowOTPModal] = useState<boolean>(false);
  const [otpCode, setOtpCode] = useState<string>("");
  const [otpLoading, setOtpLoading] = useState<boolean>(false);
  const [otpStatus, setOtpStatus] = useState<OTPStatus | null>(null);
  const [recentlyAssignedOrder, setRecentlyAssignedOrder] = useState<number | null>(null);

  const fetchFinishedOrders = async (): Promise<Order[]> => {
  try {
    const response = await apiAxios.get(`${API_BASE_URL}/Patient/GetFinishedOrders`);

    // Axios already parses JSON
    const result = response.data;

    if (result.isSuccess && result.data) {
      return result.data;
    } else {
      toast.error(result.status || "No finished orders found");
      return [];
    }
  } catch (error: any) {
    console.warn("Error fetching finished orders:", error);

    // Better error handling
    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to load finished orders";

    toast.error(message);
    return [];
  }
};

 const fetchDeliveryPartners = async (): Promise<DeliveryPartner[]> => {
  try {
    const response = await apiAxios.get(
      `${API_BASE_URL}/Patient/delivery-partners`
    );

    const result = response.data; // ✅ Axios gives parsed JSON

    if (Array.isArray(result)) {
      return result;
    } else {
      toast.error("Failed to load delivery partners");
      return [];
    }
  } catch (error: any) {
    console.warn("Error fetching delivery partners:", error);

    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to load delivery partners";

    toast.error(message);
    return [];
  }
};


 const fetchOTPStatus = async (patientId: number) => {
  try {
    const response = await apiAxios.get(
      `${API_BASE_URL}/Patient/otp-status/${patientId}`
    );

    const result = response.data; // ✅ Axios parsed JSON

    if (result?.isSuccess && result?.singleData) {
      setOtpStatus(result.singleData);
      return result.singleData;
    } else {
      toast.error(result?.message || "Failed to get OTP status");
      return null;
    }
  } catch (error: any) {
    console.warn("Error fetching OTP status:", error);

    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to get OTP status";

    toast.error(message);
    return null;
  }
};

const verifyOTP = async (patientId: number, otpCode: string) => {
  try {
    setOtpLoading(true);

    const response = await apiAxios.post(
      `${API_BASE_URL}/Patient/verify-otp`,
      {
        patientID: patientId,
        OTPCode: parseInt(otpCode),
        verificationFor: "Pickup"
      }
    );

    const result = response.data; // ✅ Axios parsed response

    if (result?.isSuccess) {
      toast.success("Pickup OTP verified successfully! Order is now picked up.");
      setShowOTPModal(false);
      setOtpCode("");

      const updatedOrders = await fetchFinishedOrders();
      setOrders(updatedOrders);
    } else {
      toast.error(result?.status || "Failed to verify OTP");
    }
  } catch (error: any) {
    console.warn("Error verifying OTP:", error);

    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to verify OTP";

    toast.error(message);
  } finally {
    setOtpLoading(false);
  }
};
 const assignDeliveryPartner = async (
  patientId: number,
  deliveryPartnerId: number
) => {
  try {
    setAssignLoading(true);

    const { data } = await apiAxios.post(
      `${API_BASE_URL}/Patient/assign-delivery-partner`,
      {
        patientID: patientId,
        deliveryPartnerID: deliveryPartnerId,
      }
    );

    if (data?.isSuccess) {
      toast.success(
        data.status || "Delivery partner assigned successfully!"
      );

      setRecentlyAssignedOrder(patientId);

      const updatedOrders = await fetchFinishedOrders();
      setOrders(updatedOrders);

      setShowAssignModal(false);
      setShowOTPModal(true);

      await fetchOTPStatus(patientId);
    } else {
      toast.error(data?.status || "Failed to assign delivery partner");
    }
  } catch (error: any) {
    console.warn("Error assigning delivery partner:", error);

    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to assign delivery partner";

    toast.error(message);
  } finally {
    setAssignLoading(false);
  }
};

  const handleAssignClick = (order: Order) => {
    setSelectedOrder(order);
    setSelectedPartner(0);
    setShowAssignModal(true);
  };

  const handleAssignConfirm = () => {
    if (selectedOrder && selectedPartner > 0) {
      assignDeliveryPartner(selectedOrder.patientID, selectedPartner);
    } else {
      toast.error("Please select a delivery partner");
    }
  };

  // ====================== DATATABLE CUSTOM STYLES ======================
  const customStyles = {
    // headRow: {
    //   style: {
    //     backgroundColor: "#3D8BFD",
    //     color: "white",
    //     borderTopLeftRadius: "8px",
    //     borderTopRightRadius: "8px",
    //   },
    // },

    headCells: {
      style: {
        fontSize: "15px",
        justifyContent: "center",
        // fontWeight: 600,
      },
    },

    // rows: {
    //   style: {
    //     minHeight: "52px",
    //   },
    //   stripedStyle: {
    //     backgroundColor: "#EEF4FF", 
    //   },
    // },

    cells: {
      style: {
        fontSize: "14px",
      },
    },
  };

  // OPTIONAL → row highlight
  // const conditionalRowStyles = [
  //   {
  //     when: (row: { status: string; }) => row.status === "Delivered",
  //     style: {
  //       backgroundColor: "#e9ffe9",
  //     },
  //   },
  // ];

  const handleVerifyOTP = () => {
    if (!selectedOrder) return;

    if (otpCode.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    verifyOTP(selectedOrder.patientID, otpCode);
  };


const generateOTP = async (patientId: number) => {
  try {
    setOtpLoading(true);

    const { data } = await apiAxios.post(
      `${API_BASE_URL}/Patient/generate-pickup-otp`,
      patientId, // ⚠️ sending raw number (same as your fetch body)
      
    );

    if (data?.isSuccess) {
      toast.success("OTP generated successfully!");
      await fetchOTPStatus(patientId);
    } else {
      toast.error(data?.status || "Failed to generate OTP");
    }
  } catch (error: any) {
    console.warn("Error generating OTP:", error);

    const message =
      error?.response?.data?.message ||
      error?.message ||
      "Failed to generate OTP";

    toast.error(message);
  } finally {
    setOtpLoading(false);
  }
};

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const [ordersData, partnersData] = await Promise.all([
        fetchFinishedOrders(),
        fetchDeliveryPartners()
      ]);

      setOrders(ordersData);
      setDeliveryPartners(partnersData);
      setLoading(false);
    };

    loadData();
  }, []);

  const columns: TableColumn<Order>[] = [
    {
      name: "Order ID",
      selector: (row) => `ORD-${row.patientID}`,
      sortable: true,
      center: true,
      width: "120px"
    },
    {
      name: "Patient Name",
      selector: (row) => row.patientName,
      sortable: true,
      center: true,
      width: "150px",
    },
    {
      name: "Age",
      selector: (row) => row.age,
      sortable: true,
      center: true,
      width: "80px"
    },
    {
      name: "Gender",
      selector: (row) => row.gender,
      sortable: true,
      center: true,
      width: "100px"
    },
    {
      name: "Tooth",
      selector: (row) => row.tooth || "N/A",
      center: true,
      sortable: true
    },
    {
      name: "Material",
      selector: (row) => row.materialName || "N/A",
      center: true,
      sortable: true
    },
    {
      name: "Shade",
      selector: (row) => row.shadeName || "N/A",
      sortable: true,
      center: true,
      width: "150px"
    },
    {
      name: "Clinic",
      selector: (row) => row.clinicName || "N/A",
      sortable: true,
      center: true,
      width: "150px"
    },
    {
      name: "Created Date",
      selector: (row) => new Date(row.createdDate).toLocaleDateString('en-IN'),
      sortable: true,
      center: true,
      width: "180px"
    },
    {
      name: "Delivery Partner",
      selector: (row) => row.deliveryPartnerName || "Not Assigned",
      sortable: true,
      width: "160px",
      center: true,
      cell: (row) => (
        <span className={`badge ${row.deliveryPartnerName ? 'bg-success' : 'bg-secondary'}`}>
          {row.deliveryPartnerName || "Not Assigned"}
        </span>
      )
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      center: true,
      width: "130px",
      cell: (row) => (
        <span className={`badge ${row.status === 'Delivered' ? 'bg-success' :
            row.status === 'Picked Up' ? 'bg-warning' :
              row.status === 'Assigned to Delivery' ? 'bg-info' :
                'bg-success'
          }`}>
          {row.status}
        </span>
      )
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="btn-group" role="group">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => handleAssignClick(row)}
            disabled={row.deliveryPartnerID !== null && row.deliveryPartnerID !== undefined}
            title={row.deliveryPartnerID ? "Already assigned" : "Assign Delivery Partner"}
          >
            {row.deliveryPartnerID ? "Assigned" : "Assign"}
          </button>

          {row.deliveryPartnerID && row.status === 'Assigned to Delivery' && (
            <button
              className="btn btn-warning btn-sm"
              onClick={() => {
                setSelectedOrder(row);
                setShowOTPModal(true);
                fetchOTPStatus(row.patientID);
              }}
              title="Verify Pickup OTP"
            >
              Verify OTP
            </button>
          )}
        </div>
      ),
      width: "120px",
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      center: true,
    },
  ];

  if (loading) {
    return (
      <div className="container mt-3">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading finished orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className=" ">
      <h4 className="mb-3">Finished Orders</h4>

      <div className="card shadow-sm p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Total Orders: {orders.length}</h5>
          <small className="text-muted">
            Orders ready for delivery assignment
          </small>
        </div>

        <DataTable
          columns={columns}
          data={orders}
          progressPending={loading}
          pagination
          highlightOnHover
          responsive
          // className="finished-orders-table"
          customStyles={customStyles}
          // conditionalRowStyles={conditionalRowStyles}
          noDataComponent={
            <div className="text-center py-4">
              <i className="bi bi-inbox fs-1 text-muted"></i>
              <p className="mt-2 text-muted">No finished orders found</p>
            </div>
          }
        />





      </div>
      {showAssignModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Assign Delivery Partner</h5>
                {/* <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setShowAssignModal(false)}
                  ></button> */}

                <button
                  type="button"
                  className="btn-close custom-close-btn"
                  data-bs-dismiss="modal"
                  aria-label="Close" onClick={() => setShowAssignModal(false)}
                ><i className="bi bi-x customcross"></i></button>


              </div>
              <div className="modal-body">
                {selectedOrder && (
                  <>
                    <div className="mb-3 text-left">
                      <strong>Order Details:</strong>
                      <div className="mt-2 custombdrbtm">
                        <p className="mb-1">Order ID: ORD-{selectedOrder.patientID}</p>
                        <p className="mb-1">Patient: {selectedOrder.patientName}</p>
                        <p className="mb-1">Clinic: {selectedOrder.clinicName || "N/A"}</p>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label htmlFor="deliveryPartner" className="form-label">
                        Select Delivery Partner <span className="red">*</span>
                      </label>
                      <select
                        id="deliveryPartner"
                        className="form-select"
                        value={selectedPartner}
                        onChange={(e) => setSelectedPartner(Number(e.target.value))}
                      >
                        <option value={0}>Choose a delivery partner...</option>
                        {deliveryPartners.map((partner) => (
                          <option key={partner.deliveryPartnerID} value={partner.deliveryPartnerID}>
                            {partner.name} - {partner.mobileNumber}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAssignConfirm}
                  disabled={assignLoading || selectedPartner === 0}
                >
                  {assignLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Assigning...
                    </>
                  ) : (
                    "Assign Partner & Generate OTP"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAssignModal(false)}
                  disabled={assignLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {showOTPModal && selectedOrder && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Pickup OTP Verification</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowOTPModal(false);
                    setOtpCode("");
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <strong>Order Details:</strong>
                  <div className="mt-2">
                    <p className="mb-1">Order ID: ORD-{selectedOrder.patientID}</p>
                    <p className="mb-1">Patient: {selectedOrder.patientName}</p>
                    <p className="mb-1">Clinic: {selectedOrder.clinicName || "N/A"}</p>

                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="otpCode" className="form-label">
                    Enter Pickup OTP  <span className="red-strick">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg "
                    id="otpCode"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                  />
                  <div className="form-text pt-2">
                    Enter the OTP received from the clinic for order pickup
                  </div>
                </div>

                {otpStatus && (
                  <div className="alert alert-info">
                    <strong>OTP Status:</strong>
                    <div className="mt-1">
                      <small>
                        Pickup Verified: {otpStatus.pickupOTPVerified ? ' Yes' : ' No'}
                      </small>
                    </div>
                  </div>
                )}

                <div className="d-flex  gap-2">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleVerifyOTP}
                    disabled={otpLoading || otpCode.length !== 6}
                  >
                    {otpLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Verifying...
                      </>
                    ) : (
                      "Verify Pickup OTP"
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => generateOTP(selectedOrder.patientID)}
                    disabled={otpLoading}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Resend OTP
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>


  );
};

export default FinishedOrdersPage;